import {
  TypeCheckoutConfiguration,
  TypePackageAddOnItemSkeleton,
} from "@contentful/types";
import { CheckoutTotals } from "@yardzen/rest-api-client";
import { Badge } from "@yardzen/ui/components/badge";
import { Checkbox } from "@yardzen/ui/components/checkbox";
import { H5 } from "@yardzen/ui/components/h5";
import { Label } from "@yardzen/ui/components/label";
import { cn } from "@yardzen/ui/lib/utils";
import { Entry } from "contentful";

export function PackageDisplayAddOns({
  currentAddOns,
  paymentIntent,
  contentfulAddons,
  onToggleAddon,
  isLoading = false,
  checkoutConfiguration,
}: {
  currentAddOns: string[];
  paymentIntent: CheckoutTotals;
  contentfulAddons:
    | (
        | Entry<
            TypePackageAddOnItemSkeleton,
            "WITHOUT_UNRESOLVABLE_LINKS",
            string
          >
        | undefined
      )[]
    | undefined;
  onToggleAddon?: (stripeProductId: string, checked: boolean) => void;
  selectedAddonNames?: string[];
  isLoading?: boolean;
  checkoutConfiguration?: TypeCheckoutConfiguration<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const enrichedAddons = paymentIntent.addons?.map((addon) => {
    const contentfulAddon = contentfulAddons?.find(
      (cAddon) => cAddon?.fields.stripeProductId === addon?.key,
    );
    return {
      ...addon,
      contentful: contentfulAddon,
    };
  });

  if (enrichedAddons?.length === 0) {
    return null;
  }

  return (
    <div className="bg-brand-beige-lighter rounded-lg p-6">
      {paymentIntent.addons && (
        <div>
          <H5 className="font-display mb-4 text-left text-2xl font-normal">
            {checkoutConfiguration?.fields.addOnHeader ?? "Complete your plan"}
          </H5>
          {enrichedAddons?.map((addonItem, i) => {
            const stripeProductId =
              addonItem.contentful?.fields.stripeProductId;
            const isChecked = currentAddOns.includes(stripeProductId ?? "");
            const isFreeAddon =
              paymentIntent.discount?.freeAddon === stripeProductId;
            const addOnContentfulEntry = addonItem.contentful;

            return (
              addonItem &&
              addOnContentfulEntry && (
                <div
                  key={`PDI-addon-${addOnContentfulEntry.fields.uniqueIdentifier}-${i}`}
                  className="items-top border-system-grayscale-30 mb-4 flex space-x-2 rounded-lg border border-dashed p-4"
                >
                  <Checkbox
                    id={`PDI-addon-checkbox-${addOnContentfulEntry.fields.uniqueIdentifier}-${i}`}
                    className="mt-0.5"
                    checked={isChecked}
                    disabled={isLoading || isFreeAddon}
                    onCheckedChange={(checked) => {
                      if (stripeProductId && onToggleAddon) {
                        onToggleAddon(stripeProductId, !!checked);
                      }
                    }}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor={`PDI-addon-checkbox-${addOnContentfulEntry.fields.uniqueIdentifier}-${i}`}
                      className="text-sm font-semibold"
                    >
                      {addOnContentfulEntry.fields?.heading}
                      {addonItem?.price && (
                        <Badge
                          variant="secondary"
                          className="bg-brand-moss-greens-20 !font-display !text-xxs ml-2 !px-2 !py-0"
                        >
                          <span
                            className={cn(
                              "text-sm",
                              isFreeAddon && "line-through",
                            )}
                          >
                            + ${addonItem?.price / 100}
                          </span>
                        </Badge>
                      )}
                    </Label>
                    <p className="text-brand-moss-greens-90 text-sm">
                      {addOnContentfulEntry.fields?.subheading}
                    </p>
                  </div>
                </div>
              )
            );
          })}
        </div>
      )}
    </div>
  );
}
